import React, { useEffect, useState, useCallback } from 'react';
import { Breadcrumb } from '@new/common/ui/Breadcrumb/Breadcrumb';
import connectToSockets from '@services/socketService';
import { Role } from '@types';
import Spinner from '@components/Spinner';
import { NORMAL_WIDTH } from './constants';

import './styles.less';
import { isUserOnboarded } from '@utils/helpers';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { Header } from './header/Header';
import { Sidebar } from './Sidebar/Sidebar';
import { useCurrentUser } from '@hooks/users';
import { AbilityProvider } from '@new/casl';
import { ScrollArea } from 'radix-ui';
import { VideoCallProvider } from 'src/contexts/VideoCallProvider';
interface AppLayoutProps {
  children: any;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: user, isLoading } = useCurrentUser();
  const [collapsed, setCollapsed] = useState(NORMAL_WIDTH >= window.innerWidth);
  const handleCollapse = useCallback(() => {
    if (NORMAL_WIDTH >= window.innerWidth) {
      setCollapsed(true);
    } else {
      setCollapsed(collapsed);
    }
  }, [collapsed]);

  useEffect(() => {
    window.addEventListener('resize', handleCollapse);
    return () => window.removeEventListener('resize', handleCollapse);
  }, [handleCollapse]);

  useEffect(() => {
    if (user) {
      connectToSockets();
    }
  }, [user]);

  useEffect(() => {
    if (!isLoading && !user) {
      navigate({ to: '/login' });
    }
  }, [isLoading, user, navigate]);

  useEffect(() => {
    if (
      user &&
      user.onboarding &&
      !isUserOnboarded(user.onboarding) &&
      location.pathname !== '/onboarding' &&
      user.role === Role.Student
    ) {
      navigate({ to: '/onboarding' });
      return;
    }
  }, [user, location.pathname, navigate]);

  if (isLoading && !user) {
    return <Spinner showLogo />;
  }

  if (!user) {
    return null;
  }

  return (
    <AbilityProvider user={user}>
      <VideoCallProvider>
        <div className="flex w-full h-screen overflow-hidden">
          <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((prev) => !prev)} />
          <div className="flex flex-col flex-1 h-screen overflow-hidden max-w-full w-full">
            <Header user={user} />
            <ScrollArea.Root className="flex-1 border-[1.5px] border-(--neutral-black-NB100) overflow-hidden">
              <ScrollArea.Viewport className="w-full h-full overflow-scroll bg-[#FAFDFF]">
                <Breadcrumb />
                {children}
              </ScrollArea.Viewport>
              <ScrollArea.Scrollbar
                orientation="vertical"
                className="flex w-2.5 bg-transparent p-0.5 hover:bg-(--neutral-black-NB100) transition-colors"
              >
                <ScrollArea.Thumb className="flex-1 bg-(--neutral-black-NB300) rounded-full hover:bg-(--neutral-black-NB400) transition-colors" />
              </ScrollArea.Scrollbar>
              <ScrollArea.Scrollbar
                orientation="horizontal"
                className="flex h-2.5 bg-transparent p-0.5 hover:bg-(--neutral-black-NB100) transition-colors"
              >
                <ScrollArea.Thumb className="flex-1 bg-(--neutral-black-NB300) rounded-full hover:bg-(--neutral-black-NB400) transition-colors" />
              </ScrollArea.Scrollbar>
              <ScrollArea.Corner className="bg-(--neutral-black-NB100)" />
            </ScrollArea.Root>
          </div>
        </div>
      </VideoCallProvider>
    </AbilityProvider>
  );
};

export const ConnectedAppLayout = AppLayout;
